import type { LoginRequest, LoginResponse, AuthStateResponse } from '~/types'

const AUTH_TOKEN_KEY = 'auth-token'

// Cookie options for auth token
const COOKIE_OPTIONS = {
  maxAge: 60 * 60 * 24 * 7, // 7 days
  path: '/',
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production',
}

export const useAuth = () => {
  const toast = useToast()
  const requestLogger = useRequestLogger()

  // Use useState for SSR-compatible reactive state
  // Cookie is used for persistence, useState for in-memory session use
  const authCookie = useCookie<string | null>(AUTH_TOKEN_KEY, COOKIE_OPTIONS)
  const token = useState<string | null>('auth-token', () => authCookie.value || null)
  // Store full auth state (login, accountType, accountId, accountEmail)
  const authState = useState<AuthStateResponse | null>('auth-state', () => null)
  const accountType = computed(() => authState.value?.accountType ?? 'USER')
  const isAuthenticated = computed(() => !!token.value && (authState.value?.login ?? true))

  const clearAuth = () => {
    authCookie.value = null
    token.value = null
    authState.value = null
  }

  // Initialize token from cookie (called on app mount to sync state)
  const initAuth = () => {
    if (authCookie.value && !token.value) {
      token.value = authCookie.value
    }
    // Try to fetch full auth state if token present
    if (token.value) {
      void fetchAuthState()
    }
  }

  const fetchAuthState = async (): Promise<boolean> => {
    const url = '/api/authentication/state'
    const method = 'GET'
    const { correlationId } = requestLogger.logFetchRequest(method, url)
    try {
      const response = await $fetch<{ results?: { state?: AuthStateResponse } } & AuthStateResponse>(url, {
        method,
        headers: getAuthHeaders(),
      })

      requestLogger.logFetchResponse(correlationId, method, url, 200, response)

      // Handle wrapped ({ results: { state: ... } }) or direct response
      const state = response?.results?.state ?? response
      if (!state?.login) {
        clearAuth()
        return false
      }

      authState.value = {
        login: true,
        accountType: state?.accountType || 'USER',
        accountId: state?.accountId || '',
        accountEmail: state?.accountEmail || '',
      }
      return true
    } catch (error: unknown) {
      const err = error as { status?: number }
      requestLogger.logFetchError(correlationId, method, url, error, err?.status)
      clearAuth()
      console.warn('Failed to fetch authentication state:', error)
      return false
    }
  }

  const login = async (credentials: LoginRequest): Promise<boolean> => {
    const url = '/api/authentication/login'
    const method = 'POST'
    const { correlationId } = requestLogger.logFetchRequest(method, url, { email: credentials.email })

    try {
      const response = await $fetch<LoginResponse>(url, {
        method,
        body: credentials,
      })

      requestLogger.logFetchResponse(correlationId, method, url, 200, { success: response.success })

      if (response.success && response.results?.token) {
        // Store token in cookie and in-memory state
        authCookie.value = response.results.token
        token.value = response.results.token

        // Fetch and store full auth state
        const stateOk = await fetchAuthState()
        if (!stateOk) {
          toast.add({
            title: 'Error',
            description: 'Session could not be validated. Please log in again.',
            color: 'error',
          })
          return false
        }

        toast.add({
          title: 'Success',
          description: 'Logged in successfully',
          color: 'success',
        })

        return true
      }

      toast.add({
        title: 'Error',
        description: response.message || 'Login failed',
        color: 'error',
      })
      return false
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; status?: number }
      requestLogger.logFetchError(correlationId, method, url, error, err.status)

      toast.add({
        title: 'Error',
        description: err.data?.message || 'Login failed',
        color: 'error',
      })
      return false
    }
  }

  const logout = () => {
    // Clear both cookie and in-memory state
    clearAuth()

    toast.add({
      title: 'Logged out',
      description: 'You have been logged out successfully',
      color: 'info',
    })

    navigateTo('/login')
  }

  const getAuthHeaders = (): Record<string, string> => {
    if (token.value) {
      return { Authorization: `Bearer ${token.value}` }
    }
    return {}
  }

  return {
    token: readonly(token),
    isAuthenticated,
    accountType,
    authState: readonly(authState),
    initAuth,
    login,
    logout,
    getAuthHeaders,
  }
}
